/* ============================================
   TareaDetalle — la tarea abierta, dentro de su propia pestaña

   No es una ruta ni un modal: reemplaza la tabla y vuelve con la flecha. Una
   ruta sacaría al usuario de la oportunidad para leer una línea de trabajo que
   pertenece a esa oportunidad, y un modal taparía la lista que se estaba
   mirando.

   Todo se guarda al salir del campo (`onBlur`), como la tabla. No hay botón de
   Guardar porque no hay nada que confirmar: son campos sueltos, no un
   formulario con un resultado.
   ============================================ */
import { useState } from 'react'
import { useApi } from '@/hooks/useApi'
import type { Tarea, EstadoTarea, PrioridadTarea } from '@/lib/types'
import { ESTADOS_ASIGNABLES, ESTADO_TAREA_META } from '@/lib/tareas-estado'
import { PRIORIDADES_TAREA } from '@/lib/types'
import { Badge } from '@/components/ui'
import LeadAdjuntos from './LeadAdjuntos'
import { IconArrowLeft, IconTrash, IconPaperclip } from '@/components/icons/Icons'

interface TareaDetalleProps {
  leadId: number
  tarea: Tarea
  onVolver: () => void
  /** Avisa el cambio para que la lista de atrás no quede desactualizada. */
  onCambio: (cambios: Partial<Tarea>) => void
  onEliminar: (t: Tarea) => void
}

export default function TareaDetalle({ leadId, tarea, onVolver, onCambio, onEliminar }: TareaDetalleProps) {
  const api = useApi()
  const [titulo, setTitulo] = useState(tarea.titulo)
  const [descripcion, setDescripcion] = useState(tarea.descripcion || '')
  const [notas, setNotas] = useState(tarea.notas || '')
  const [error, setError] = useState<string | null>(null)

  async function guardar(cambios: Partial<Tarea>) {
    setError(null)
    try {
      await api.put(`/api/tareas/${tarea.id}`, cambios)
      onCambio(cambios)
    } catch (err: any) {
      console.error('Error guardando la tarea:', err)
      setError(err?.message || 'No se pudo guardar el cambio')
    }
  }

  /** Solo si cambió: salir de un campo que no se tocó no es un guardado. */
  function guardarSiCambio(campo: keyof Tarea, valor: string) {
    const limpio = valor.trim()
    const actual = (tarea[campo] as string | null) || ''
    if (limpio === actual) return
    guardar({ [campo]: limpio || null } as Partial<Tarea>)
  }

  const meta = ESTADO_TAREA_META[tarea.estado] ?? ESTADO_TAREA_META.pendiente

  return (
    <div className="tarea-detalle">
      <div className="tarea-detalle__barra">
        <button type="button" className="btn btn-sm btn-secondary" onClick={onVolver}>
          <IconArrowLeft size={14} /> Tareas
        </button>
        <Badge variant={meta.variant}>{meta.label}</Badge>
        <button
          type="button"
          className="btn btn-sm btn-danger tarea-detalle__borrar"
          onClick={() => onEliminar(tarea)}
        >
          <IconTrash size={13} /> Eliminar
        </button>
      </div>

      {error && <p className="ld-tareas__error">{error}</p>}

      <input
        className="tarea-detalle__titulo"
        value={titulo}
        onChange={e => setTitulo(e.target.value)}
        onBlur={() => {
          const limpio = titulo.trim()
          // Un título vacío dejaría la fila sin nada que leer: se revierte.
          if (!limpio) { setTitulo(tarea.titulo); return }
          if (limpio !== tarea.titulo) guardar({ titulo: limpio })
        }}
        placeholder="Título de la tarea"
      />

      <div className="tarea-detalle__campos">
        <label className="tarea-detalle__campo">
          <span className="tarea-detalle__label">Estado</span>
          <select
            className="form-input"
            value={tarea.estado}
            onChange={e => guardar({
              estado: e.target.value as EstadoTarea,
              // La fecha de cierre la pone el estado, no una persona.
              fecha_completada: e.target.value === 'completada'
                ? new Date().toISOString().slice(0, 10)
                : null,
            })}
          >
            {ESTADOS_ASIGNABLES.map(e => (
              <option key={e} value={e}>{ESTADO_TAREA_META[e].label}</option>
            ))}
          </select>
        </label>

        <label className="tarea-detalle__campo">
          <span className="tarea-detalle__label">Prioridad</span>
          <select
            className="form-input"
            value={tarea.prioridad}
            onChange={e => guardar({ prioridad: e.target.value as PrioridadTarea })}
          >
            {PRIORIDADES_TAREA.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>

        <label className="tarea-detalle__campo">
          <span className="tarea-detalle__label">Desde</span>
          <input
            type="date"
            className="form-input"
            value={tarea.fecha_inicio?.slice(0, 10) || ''}
            onChange={e => guardar({ fecha_inicio: e.target.value || null })}
          />
        </label>

        <label className="tarea-detalle__campo">
          <span className="tarea-detalle__label">Hasta</span>
          <input
            type="date"
            className="form-input"
            value={tarea.fecha_limite?.slice(0, 10) || ''}
            onChange={e => guardar({ fecha_limite: e.target.value || null })}
          />
        </label>
      </div>

      <div className="tarea-detalle__bloque">
        <span className="tarea-detalle__label">Descripción</span>
        <textarea
          className="form-input tarea-detalle__texto"
          value={descripcion}
          onChange={e => setDescripcion(e.target.value)}
          onBlur={() => guardarSiCambio('descripcion', descripcion)}
          placeholder="Qué hay que hacer, con qué detalle..."
          rows={4}
        />
      </div>

      <div className="tarea-detalle__bloque">
        <span className="tarea-detalle__label">Notas</span>
        <textarea
          className="form-input tarea-detalle__texto"
          value={notas}
          onChange={e => setNotas(e.target.value)}
          onBlur={() => guardarSiCambio('notas', notas)}
          placeholder="Lo que fue pasando con esta tarea..."
          rows={3}
        />
      </div>

      <div className="tarea-detalle__bloque">
        <span className="tarea-detalle__label"><IconPaperclip size={12} /> Adjuntos</span>
        <LeadAdjuntos leadId={leadId} tareaId={tarea.id} />
      </div>
    </div>
  )
}
